import {action, makeObservable, observable} from "mobx";
import {TopicConfiguration, TopicLayersConfiguration} from "../../config/topic-configuration";
import {NavigationConfiguration} from "../../config/navigation-configuration";
import {MapViewerConfiguration} from "../../config/map-viewer-configuration";
import MatomoTracker from "@datapunt/matomo-tracker-js";
import {NavigateFunction} from "react-router";
import {Authentication} from "../../core/authentication";
import _ from "lodash";
import * as React from "react";

// eslint-disable-next-line no-shadow
export enum MessageBoxType {
    Standard = "standard",
    Info = "info",
    Success = "success",
    Warning = "warning",
    Error = "error"
}

export interface MessageBoxProps {
    type?: MessageBoxType;
    title: string;
    message: string;
    subTitle?: string;
    hasProgress?: boolean;
    percent?: number;
    okButton?: boolean;
    cancelButton?: boolean;
}

export class AppState {
    // The currently activated topic
    topic: TopicConfiguration;
    location: Location;
    match = {};
    navigate: NavigateFunction;
    pageTitle: string;
    topContent: React.ReactElement | React.ReactElement[] | null;
    bottomContent: React.ReactElement | null;
    asideContent: React.ReactElement | null;
    breadcrumbs: NavigationConfiguration = new NavigationConfiguration();
    subtopic?: TopicConfiguration;
    subtopicId?: string;
    subtopicLabel?: string;
    objectId?: string;
    objectLabel?: string;

    authentication: Authentication;
    /**
     * If user is logged in to portal
     * is checked at application startup with IdentityManager
     */
    loggedIn: boolean;
    esriCredential: __esri.Credential | null;
    messageBox = {
        type: MessageBoxType.Standard,
        show: false,
        title: "",
        message: "",
        subTitle: "",
        hasProgress: false,
        percent: 0,
        okButton: true,
        cancelButton: false,
    };
    mapViewerConfig: MapViewerConfiguration;
    tracker: MatomoTracker | undefined;

    constructor(mapViewerConfig: MapViewerConfiguration, tracker?: MatomoTracker) {
        this.mapViewerConfig = mapViewerConfig;
        this.tracker = tracker;
        makeObservable(this, {
            topic: observable,
            location: observable,
            match: observable,
            navigate: observable,
            pageTitle: observable,
            topContent: observable,
            bottomContent: observable,
            asideContent: observable,
            breadcrumbs: observable,
            subtopic: observable,
            subtopicId: observable,
            subtopicLabel: observable,
            objectId: observable,
            objectLabel: observable,
            authentication: observable,
            loggedIn: observable,
            esriCredential: observable,
            messageBox: observable,

            setRoute: action,
            removeRouteHash: action,
            setRoutePath: action,
            setTopic: action,
            setTopicById: action,
            setTopicByRoute: action,
            setSubtopic: action,
            setSubtopicId: action,
            setSubtopicLabel: action,
            setSubtopicLayers: action,
            setPageTitle: action,
            setTopContent: action,
            setBottomContent: action,
            setAsideContent: action,
            setBreadcrumbs: action,
            setObjectId: action,
            setObjectLabel: action,
            setAuthentication: action,
            setLoggedIn: action,
            showMessageBox: action,
            closeMessageBox: action,
            progressMessageBox: action
        });
    }

    setRoute(location: Location, match: object, navigate: NavigateFunction): void {
        const previousPathname = this.location?.pathname;
        this.location = location;
        this.match = match;
        this.navigate = navigate;
        if (this.tracker !== undefined && this.location.pathname !== previousPathname) {
            // TODO: Timeout durch zuverlässigere Lösung ersetzten
            setTimeout(() => {
                if (this.tracker !== undefined) { this.tracker.trackPageView();}
            }, 200);
        }
    }

    removeRouteHash(): void {
        if (this.tracker !== undefined) {
            this.tracker.trackPageView();
        }
        this.location.hash = "";
        this.navigate(this.location.pathname);
    }

    setRoutePath(pathname: string): void {
        if (this.location.pathname !== pathname) {
            this.navigate(pathname);
            this.location.pathname = pathname;
            if (this.tracker !== undefined) {
                // TODO: Timeout durch zuverlässigere Lösung ersetzten
                setTimeout(() => {
                    if (this.tracker !== undefined) { this.tracker.trackPageView();}
                }, 200);
            }
        }
    }

    getRoute(): string {
        return this.location.pathname;
    }

    setTopic(topic: TopicConfiguration): void {
        this.topic = topic;
    }

    setTopicById(topicId: string): void {
        const topic = this.mapViewerConfig.getTopic(topicId);
        if (topic) {
            this.topic = topic;
        }
    }

    setTopicByRoute(route: string): TopicConfiguration | void {
        const topic = this.mapViewerConfig.getTopicByRoute(`/${route}`);
        if (topic) {
            this.topic = topic;
        }
        return topic;
    }

    setSubtopic(subtopic?: TopicConfiguration | null): void {
        if (subtopic) {
            subtopic.revision = Math.random().toString(); // Neues Subtopic erzwingen
            this.subtopic = subtopic;
            this.subtopicId = subtopic.id;
            this.subtopicLabel = subtopic.label? subtopic.label: subtopic.name;
        } else {
            this.subtopic = undefined;
            this.subtopicId = undefined;
            this.subtopicLabel = undefined;
        }
    }

    setSubtopicId(subtopicId: string, subtopicLabel?: string): void {
        this.subtopicId = subtopicId;
        if (typeof subtopicLabel === "string") {
            this.subtopicLabel = subtopicLabel;
        }
    }

    setSubtopicLabel(subtopicLabel?: string): void {
        this.subtopicLabel = subtopicLabel;
    }

    setSubtopicLayers(subtopicLayers: TopicLayersConfiguration): void {
        if(this.subtopic && !_.isEqual(this.subtopic.layers, subtopicLayers)){
            this.subtopic.layers = subtopicLayers;
            this.subtopic.revision = Math.random().toString(); // Neues Subtopic erzwingen
        }
    }

    setPageTitle(title: string): void {
        this.pageTitle = title;
    }

    setTopContent(content: React.ReactElement | React.ReactElement[] | null): void {
        this.topContent = content;
    }

    setBottomContent(content: React.ReactElement | null): void {
        this.bottomContent = content;
    }

    setAsideContent(content: React.ReactElement | null): void {
        this.asideContent = content;
    }

    setBreadcrumbs(breadcrumbs: NavigationConfiguration): void {
        this.breadcrumbs = breadcrumbs;
    }

    setObjectId(objectId: string, objectLabel?: string): void {
        this.objectId = objectId;
        if (typeof objectLabel === "string") {
            this.objectLabel = objectLabel;
        }
    }

    setObjectLabel(objectLabel?: string): void {
        this.objectLabel = objectLabel;
    }

    setAuthentication(authentication: Authentication): void {
        this.authentication = authentication;
    }

    setLoggedIn(loggedIn: boolean, credential?: __esri.Credential): void {
        this.loggedIn = loggedIn;
        this.esriCredential = credential ? credential : null;
    }
    /**
     * Show a message box
     *
     * @param props MessageBoxProps
     */
    showMessageBox(props: MessageBoxProps): void {
        this.messageBox.show = true;

        const {title, message, subTitle, hasProgress, percent} = props;
        this.messageBox.title = title;
        this.messageBox.message = message;
        if (subTitle) {
            this.messageBox.subTitle = subTitle;
        }
        this.messageBox.hasProgress = hasProgress || false;
        if (percent) {
            this.messageBox.percent = percent;
        }

        if (props.hasOwnProperty("type") && props.type !== undefined) {
            this.messageBox.type = props.type;
        }
        if (props.hasOwnProperty("okButton") && props.okButton !== undefined) {
            this.messageBox.okButton = props.okButton;
        }
        if (props.hasOwnProperty("cancelButton") && props.cancelButton !== undefined) {
            this.messageBox.cancelButton = props.cancelButton;
        }
    }

    /**
     * Close the message box
     */
    closeMessageBox(): void {
        this.messageBox.type = MessageBoxType.Standard;
        this.messageBox.show = false;
        this.messageBox.title = "";
        this.messageBox.message = "";
        this.messageBox.subTitle = "";
        this.messageBox.hasProgress = false;
        this.messageBox.percent = 0;
        this.messageBox.okButton = true;
        this.messageBox.cancelButton = false;
    }

    /**
     * Update progress on message box
     * Use 'hasProgress = true' to show progress bar on message box
     *
     * @param percent
     */
    progressMessageBox(percent: number): void {
        if (percent < 0) {
            percent = 0;
        }
        if (percent > 100) {
            percent = 100;
        }
        this.messageBox.percent = percent;
    }
}
